import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import WindowIcon from "@mui/icons-material/Window";
import LogoutIcon from "@mui/icons-material/Logout";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import DownloadIcon from "@mui/icons-material/Download";
import { motion } from "framer-motion";
import { useLocation, useParams } from "react-router-dom/cjs/react-router-dom";
import { useGetRealEstateDetails } from "../../helperHooks/getDetails";
import ContactDialog from "./conatctDetailsPdfDownlaod";
import ProjectGallery from "./Details/ProjectGallery";
import defaultImage from "../image/Image_not_available.png";

const logos = [
  AccountBalanceIcon,
  BackupTableIcon,
  AutoFixHighIcon,
  WindowIcon,
  LogoutIcon,
  MeetingRoomIcon,
  ElectricalServicesIcon,
];

const REDetails = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const { data: properties, loading, error } = useGetRealEstateDetails();
  const [dialogOpen, setDialogOpen] = useState(false);
  const estate =
    properties?.find((property) => String(property.id) === String(id)) ||
    state?.property ||
    null;
  const baseImageBucketUrl = process.env.REACT_APP_S3_IMAGE_BUCKET_URL;
  const image = estate?.projectImage
    ? `${baseImageBucketUrl}${estate.projectImage}`
    : defaultImage;
  const features = estate?.details?.propertiesFeatures || [];
  const specifications = estate?.details?.propertiesSpecification || [];
  const gallery = estate?.details?.imageGallery || [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (loading && !estate) {
    return (
      <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center" }}>
        <Typography color="text.secondary">Loading property details…</Typography>
      </Box>
    );
  }

  if (error && !estate) {
    return (
      <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center", px: 3 }}>
        <Typography color="error">
          We couldn&apos;t load this property. Please try again later.
        </Typography>
      </Box>
    );
  }

  if (!estate || !estate.displayDetailsPageFlag) {
    return (
      <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center" }}>
        <Typography variant="h4">Property details are not available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f6f4ef" }}>
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 440, md: 680 },
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
          backgroundColor: "#292720",
        }}
      >
        <Box
          component="img"
          src={image}
          alt={estate.projectTitle}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(20,19,16,0.58), transparent 90%), linear-gradient(0deg, rgba(20,19,16,0.75), transparent 70%)",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            padding: "clamp(28px, 7vw, 88px)",
          }}
        >
          <Typography
            sx={{
              mb: 2,
              color: "#e2c796",
              fontSize: "0.68rem",
              fontWeight: 600,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            {estate.status || "A HOMELY DEVELOPMENT"}
            {estate.location ? `  /  ${estate.location}` : ""}
          </Typography>
          <Typography
            component="h1"
            sx={{
              maxWidth: 900,
              color: "white",
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: { xs: "2.8rem", md: "5rem" },
              lineHeight: 1.05,
            }}
          >
            {estate.projectTitle}
          </Typography>
          {estate.registrationNumber && (
            <Typography
              sx={{
                mt: 2,
                color: "rgba(255,255,255,0.8)",
                fontSize: "0.78rem",
                letterSpacing: "0.08em",
              }}
            >
              Registration No. {estate.registrationNumber}
            </Typography>
          )}
        </motion.div>
      </Box>

      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 3, md: 7 }, py: { xs: 6, md: 9 } }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: -22 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              <Typography
                sx={{
                  mb: 1.5,
                  color: "#9a794b",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                }}
              >
                THE DEVELOPMENT
              </Typography>
              <Typography
                component="h2"
                variant="h3"
                sx={{ mb: 2.5, fontSize: { xs: "2.1rem", md: "3rem" } }}
              >
                Introducing {estate.projectTitle}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.95rem", md: "1rem" },
                  lineHeight: 1.9,
                  whiteSpace: "pre-line",
                }}
              >
                {estate.projectInfo}
              </Typography>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                backgroundColor: "#eeece5",
              }}
            >
              {[
                ["Location", estate.location],
                ["Area", estate.area],
                ["Status", estate.status],
              ]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 1.8,
                      borderBottom: "1px solid #d9d5ca",
                      "&:last-of-type": { borderBottom: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#8f7248",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography sx={{ color: "#292720", fontSize: "0.88rem", textAlign: "right" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              {estate.projectBrochureUrl && (
                <Button
                  variant="contained"
                  endIcon={<DownloadIcon />}
                  onClick={() => setDialogOpen(true)}
                  sx={{
                    mt: 2.5,
                    width: "100%",
                    py: 1.4,
                    borderRadius: 0,
                    backgroundColor: "#292720",
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    "&:hover": { backgroundColor: "#49463d" },
                  }}
                >
                  Request brochure
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {features.length > 0 && (
        <Box sx={{ px: { xs: 3, md: 7 }, py: { xs: 6, md: 8 }, backgroundColor: "#eeece5" }}>
          <Box sx={{ maxWidth: 1280, mx: "auto" }}>
            <Typography
              sx={{
                mb: 1.5,
                color: "#9a794b",
                fontSize: "0.68rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
              }}
            >
              CONSIDERED LIVING
            </Typography>
            <Typography
              component="h2"
              variant="h3"
              sx={{ mb: 3, fontSize: { xs: "2.1rem", md: "2.8rem" } }}
            >
              Property features
            </Typography>
            <Grid container spacing={1}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={`${feature}-${index}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.2) }}
                  >
                    <ListItem sx={{ alignItems: "flex-start", py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 28, pt: 0.5, color: "#a68757" }}>
                        <FiberManualRecordIcon sx={{ fontSize: 9 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{
                          sx: { color: "#49463d", fontSize: "0.9rem", lineHeight: 1.65 },
                        }}
                      />
                    </ListItem>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}

      {specifications.length > 0 && (
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 3, md: 7 }, py: { xs: 6, md: 9 } }}>
          <Typography
            sx={{
              mb: 1.5,
              color: "#9a794b",
              fontSize: "0.68rem",
              fontWeight: 600,
              letterSpacing: "0.18em",
            }}
          >
            THE DETAILS
          </Typography>
          <Typography
            component="h2"
            variant="h3"
            sx={{ mb: 4, fontSize: { xs: "2.1rem", md: "2.8rem" } }}
          >
            Property specifications
          </Typography>
          <Grid container spacing={2}>
            {specifications.map((specification, index) => {
              const SpecificationIcon = logos[index % logos.length];
              return (
                <Grid item xs={12} sm={6} md={4} key={`${specification.title}-${index}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.2) }}
                    style={{ height: "100%" }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        minHeight: 190,
                        p: 3,
                        backgroundColor: "#eeece5",
                        transition: "transform 250ms ease",
                        "&:hover": { transform: "translateY(-3px)" },
                      }}
                    >
                      <SpecificationIcon sx={{ mb: 2, color: "#9a794b", fontSize: 27 }} />
                      <Typography
                        component="h3"
                        sx={{
                          mb: 1,
                          color: "#292720",
                          fontFamily: 'Georgia, "Times New Roman", serif',
                          fontSize: "1.2rem",
                        }}
                      >
                        {specification.title}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", fontSize: "0.85rem", lineHeight: 1.7 }}>
                        {specification.shortDescription}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {gallery.length > 0 && <ProjectGallery gallery={gallery} />}
      <ContactDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        estate={estate}
      />
    </Box>
  );
};

export default REDetails;
